(ns app.gis.config-model
  (:require [clojure.string :as str]
            [cloudpermit-commons.schemas.general :as general]
            [core.domain.schemas :as schemas]
            [schema-tools.core :as st]
            [schema.core :as s]))

(def system-types [:arcgis :cgis :wms-wfs :azimut-arcgis :regrid-arcgis])

(def arcgis-auth-types [:arcgis.authentication/none
                        :arcgis.authentication/classic-token
                        :arcgis.authentication/oauth2])

(s/defschema GisServerConfig
  (st/merge {:gis-server-config/name        general/NonBlankString
             :gis-server-config/system      (apply s/enum system-types)
             :gis-server-config/service-url schemas/URI}
            (st/optional-keys {:gis-server-config/arcgis-token-url s/Str
                               :gis-server-config/cgis-dataset-id  s/Str
                               :gis-server-config/username         s/Str
                               :gis-server-config/password         s/Str
                               :gis-server-config/auth-method      (s/enum :gis-server-config.auth-method/arcgis-token
                                                                           :gis-server-config.auth-method/bearer-token)
                               :gis-server-config/arcgis-auth-type (apply s/enum arcgis-auth-types)})))

(s/defschema SearchConfigRole (s/enum :address-search :point-search :property-owner-search :neighbour-search :right-of-way-search))

(s/defschema GisMunicipalityFilter
  (st/merge
    {:gis-municipality-filter/municipality-number s/Str
     :gis-municipality-filter/attribute           s/Str
     :gis-municipality-filter/value               s/Str
     :gis-municipality-filter/match-type          (s/enum :equals :prefix)}
    (st/optional-keys
      {:gis-municipality-filter/attribute-type s/Str})))

(def required-mapping-keys [:property/id
                            :property/integration-id])

(def optional-mapping-keys [:property/secondary-id
                            :property/legal
                            :property/type-code
                            :property/zoning
                            :property/ward
                            :address/full-address
                            :address/street-number
                            :address/street-name
                            :property/description])

(def property-owner-search-mapping-keys [:name
                                         :address
                                         :address-multifield])

(def i18n-overridable-keys [:property/id
                            :property/integration-id
                            :property/secondary-id])

(def i18n-override-values [:property/roll-number
                           :property/pid
                           :property/pin
                           :property/municipal-id
                           :property/aan
                           :property/apn
                           :property/affectation])

(def wfs-feature-id-key
  "We augment feature's properties so that they contain this key with feature's id as a value."
  :feature-id)

(def wfs-constant-attribute-mappings
  "For WFS there is currently one constant attribute mapping which the user is not allowed to change."
  {:property/integration-id (name wfs-feature-id-key)})

(def wfs-constant-property-search-mappings
  "For WFS there is currently one constant property search mapping which the user is not allowed to change."
  {:id-attribute (name wfs-feature-id-key)})

(s/defschema CommonArcGISConfig
  "Common schema for ArcGIS service options used to form the request URL.
   :gis-service-config/arcgis-folder is optional and should be omitted when the service is not in a folder, i.e.
   it's at the root of the server."
  {(s/optional-key :gis-service-config/arcgis-folder)  s/Str
   (s/optional-key :gis-service-config/arcgis-service) s/Str
   :gis-service-config/arcgis-service-type             general/NonBlankString})

(s/defschema MappingValue
  "A mapping value can be either a simple string (attribute name) or a vector
   of [name opts] where opts may include :append or :type."
  (s/cond-pre
    general/NonBlankString
    [(s/one general/NonBlankString "name")
     (s/one {(s/optional-key :append) s/Str
             (s/optional-key :type)   s/Str}
            "opts")]))

(s/defschema ArcGISMappingConfig
  (st/merge (zipmap required-mapping-keys
                    (repeat MappingValue))
            (->> (zipmap optional-mapping-keys
                         (repeat MappingValue))
                 st/optional-keys)
            (st/optional-keys
              {:address/language s/Keyword})))

(s/defschema WFSMappingConfig
  (st/merge ArcGISMappingConfig
            {:property/integration-id (s/eq (name wfs-feature-id-key))}))

(s/defschema ArcGISPropertySearchIdMapping
  (st/merge
    {:id-attribute general/NonBlankString}
    (st/optional-keys
      {:id-type general/NonBlankString})))

(s/defschema ArcGISPropertySearchConfig
  (st/optional-keys {:property-owner-search (st/merge {:attributes (->> (zipmap property-owner-search-mapping-keys
                                                                                (repeat [general/NonBlankString]))
                                                                        st/optional-keys)}
                                                      ArcGISPropertySearchIdMapping)
                     :neighbour-search      ArcGISPropertySearchIdMapping}))

(s/defschema WFSPropertySearchConfig
  (st/assoc-in ArcGISPropertySearchConfig [s/Keyword :id-attribute] (s/eq (name wfs-feature-id-key))))

(s/defschema ArcGISSearchConfig
  (st/merge {:gis-search-config/name            general/NonBlankString
             :gis-search-config/roles           (s/conditional seq #{SearchConfigRole} 'at-least-one-role-required)
             :gis-search-config/arcgis-layer-id s/Int}
            CommonArcGISConfig
            (st/optional-keys {:gis-search-config/mapping                ArcGISMappingConfig
                               :gis-search-config/property-search-config ArcGISPropertySearchConfig
                               :gis-search-config/municipality-filters   [GisMunicipalityFilter]
                               :gis-search-config/pagination?            s/Bool
                               :gis-search-config/render-on-map?         s/Bool
                               :gis-search-config/searchable?            s/Bool})))

(s/defschema CGISSearchConfig
  (st/merge {:gis-search-config/name  general/NonBlankString
             :gis-search-config/roles (s/conditional seq #{SearchConfigRole} 'at-least-one-role-required)}
            (st/optional-keys {:gis-search-config/mapping                ArcGISMappingConfig
                               :gis-search-config/property-search-config ArcGISPropertySearchConfig})))

(s/defschema WFSSearchConfig
  (st/merge {:gis-search-config/name             general/NonBlankString
             :gis-search-config/roles            (s/conditional seq #{SearchConfigRole} 'at-least-one-role-required)
             :gis-search-config/wfs-version      general/NonBlankString
             :gis-search-config/wfs-feature-name general/NonBlankString
             :gis-search-config/wfs-feature-crs  general/NonBlankString}
            (st/optional-keys {:gis-search-config/mapping                WFSMappingConfig
                               :gis-search-config/property-search-config WFSPropertySearchConfig
                               :gis-search-config/wfs-path               s/Str
                               :gis-search-config/municipality-filters   [GisMunicipalityFilter]
                               :gis-search-config/wfs-swap-coordinates?  s/Bool})))

(s/defschema CommonMapConfig
  {:gis-map-config/name             general/NonBlankString
   :gis-map-config/ui-title         general/NonBlankString
   :gis-map-config/default-enabled? s/Bool})

(s/defschema ArcGISMapConfig
  (st/merge {:gis-map-config/arcgis-layer-ids #{s/Int}}
            CommonMapConfig
            CommonArcGISConfig))

(s/defschema WMSMapConfig
  (st/merge {:gis-map-config/wms-version               general/NonBlankString
             :gis-map-config/wms-layers                (s/conditional seq #{general/NonBlankString} 'at-least-one-layer-required)
             (s/optional-key :gis-map-config/wms-path) s/Str}
            CommonMapConfig))

(defn resolve-arcgis-auth-type [{:gis-server-config/keys [system arcgis-auth-type arcgis-token-url]}]
  (cond
    (not= system :arcgis) nil
    arcgis-auth-type arcgis-auth-type
    (str/blank? arcgis-token-url) :arcgis.authentication/none
    :else :arcgis.authentication/classic-token))

(defn mapping-name
  "Extract the GIS attribute name from a mapping value.
   Handles both string format and [name opts] format."
  [mapping-value]
  (if (vector? mapping-value)
    (first mapping-value)
    mapping-value))

(defn mapping-type
  "Extract the GIS attribute type from a mapping value.
   Returns nil for string format values."
  [mapping-value]
  (when (vector? mapping-value)
    (-> mapping-value second :type)))
